import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  AlertTriangle, 
  Clock, 
  Download, 
  Eye, 
  Filter, 
  FileText, 
  Lock, 
  Search, 
  Shield, 
  User, 
  Users, 
  Calendar, 
  BarChart2, 
  PieChart,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  AlertCircle,
  Layers,
  Cpu,
  Map
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import LoadingSpinner from '../atoms/LoadingSpinner';
import { ResponsiveGrid, ResponsiveStack } from '../molecules/ResponsiveContainer';

interface AuditLog {
  id: string;
  user_id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  status: string;
  ip_address: string;
  user_agent: string;
  details: any;
  created_at: string;
  user_email?: string;
  user_name?: string;
  blockchain_verification?: {
    transaction_hash: string;
    status: string;
  };
}

interface AuditAlert {
  id: string;
  audit_log_id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  is_resolved: boolean;
  created_at: string;
}

interface FilterOptions {
  entityType: string[];
  action: string[];
  status: string[];
  dateRange: {
    start: Date;
    end: Date;
  };
  userId?: string;
  searchTerm: string;
}

const AuditDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [alerts, setAlerts] = useState<AuditAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'logs' | 'alerts' | 'reports'>('overview');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    entityType: [],
    action: [],
    status: [],
    dateRange: {
      start: subDays(new Date(), 7),
      end: new Date()
    },
    searchTerm: ''
  });
  const [stats, setStats] = useState({
    totalLogs: 0,
    documentAccess: 0,
    willAccess: 0,
    failedAttempts: 0,
    alertsByLevel: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    },
    activityByDay: [] as {date: string, count: number}[],
    topUsers: [] as {user_id: string, user_email: string, count: number}[]
  });
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportFormat, setReportFormat] = useState<'pdf' | 'csv' | 'json'>('pdf');
  const [reportType, setReportType] = useState<'activity' | 'security' | 'compliance'>('activity');
  const [reportDateRange, setReportDateRange] = useState({
    start: subDays(new Date(), 30),
    end: new Date()
  });

  // Check if user is admin (richierodney434@gmail.com)
  const isAdmin = user?.email === 'richierodney434@gmail.com';

  useEffect(() => {
    if (isAdmin) {
      fetchAuditData();
    }
  }, [isAdmin]);

  const fetchAuditData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch audit logs
      const { data: logsData, error: logsError } = await supabase
        .from('audit_logs')
        .select(`
          *,
          user:users(email, name)
        `)
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (logsError) throw logsError;
      
      // Fetch alerts
      const { data: alertsData, error: alertsError } = await supabase
        .from('audit_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (alertsError) throw alertsError;
      
      // Fetch blockchain verifications for logs
      const logIds = logsData.map(log => log.id);
      const { data: verificationsData, error: verificationsError } = await supabase
        .from('blockchain_verifications')
        .select('*')
        .in('audit_log_id', logIds);
      
      if (verificationsError) throw verificationsError;
      
      // Map verifications to logs
      const logsWithVerifications = logsData.map(log => {
        const verification = verificationsData.find(v => v.audit_log_id === log.id);
        return {
          ...log,
          user_email: log.user?.email,
          user_name: log.user?.name,
          blockchain_verification: verification ? {
            transaction_hash: verification.transaction_hash,
            status: verification.status
          } : undefined
        };
      });
      
      // Calculate statistics
      const totalLogs = logsWithVerifications.length;
      const documentAccess = logsWithVerifications.filter(log => log.entity_type === 'document').length;
      const willAccess = logsWithVerifications.filter(log => log.entity_type === 'will').length;
      const failedAttempts = logsWithVerifications.filter(log => log.status === 'failed').length;
      
      const alertsByLevel = {
        low: alertsData.filter(alert => alert.severity === 'low').length,
        medium: alertsData.filter(alert => alert.severity === 'medium').length,
        high: alertsData.filter(alert => alert.severity === 'high').length,
        critical: alertsData.filter(alert => alert.severity === 'critical').length
      };
      
      // Activity by day
      const activityByDay = Array.from({ length: 7 }, (_, i) => {
        const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
        const count = logsWithVerifications.filter(log => 
          format(parseISO(log.created_at), 'yyyy-MM-dd') === date
        ).length;
        return { date, count };
      }).reverse();
      
      // Top users
      const userCounts: Record<string, { user_id: string, user_email: string, count: number }> = {};
      logsWithVerifications.forEach(log => {
        if (log.user_id && log.user_email) {
          if (!userCounts[log.user_id]) {
            userCounts[log.user_id] = { 
              user_id: log.user_id, 
              user_email: log.user_email, 
              count: 0 
            };
          }
          userCounts[log.user_id].count++;
        }
      });
      
      const topUsers = Object.values(userCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      setAuditLogs(logsWithVerifications);
      setAlerts(alertsData);
      setStats({
        totalLogs,
        documentAccess,
        willAccess,
        failedAttempts,
        alertsByLevel,
        activityByDay,
        topUsers
      });
      
    } catch (error: any) {
      console.error('Error fetching audit data:', error);
      setError(error.message || 'Failed to load audit data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('audit_alerts')
        .update({ 
          is_resolved: true,
          resolved_by: user?.id,
          resolved_at: new Date().toISOString()
        })
        .eq('id', alertId);
      
      if (error) throw error;
      
      // Update local state
      setAlerts(alerts.map(alert => 
        alert.id === alertId 
          ? { ...alert, is_resolved: true } 
          : alert
      ));
      
    } catch (error: any) {
      console.error('Error resolving alert:', error);
    }
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    
    try {
      // In a real implementation, this would call a backend service
      // to generate the report and return a download link
      
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create report record
      const { data, error } = await supabase
        .from('audit_reports')
        .insert({
          report_type: reportType,
          parameters: {
            dateRange: {
              start: reportDateRange.start.toISOString(),
              end: reportDateRange.end.toISOString()
            },
            format: reportFormat
          },
          generated_by: user?.id,
          format: reportFormat
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // In a real implementation, this would return a download link
      // For now, we'll just show a success message
      alert(`Report generated successfully! Report ID: ${data.id}`);
      
    } catch (error: any) {
      console.error('Error generating report:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const applyFilters = (logs: AuditLog[]) => {
    return logs.filter(log => {
      // Filter by entity type
      if (filters.entityType.length > 0 && !filters.entityType.includes(log.entity_type)) {
        return false;
      }
      
      // Filter by action
      if (filters.action.length > 0 && !filters.action.includes(log.action)) {
        return false;
      }
      
      // Filter by status
      if (filters.status.length > 0 && !filters.status.includes(log.status)) {
        return false;
      }
      
      // Filter by date range
      const logDate = new Date(log.created_at);
      if (logDate < filters.dateRange.start || logDate > filters.dateRange.end) {
        return false;
      }
      
      // Filter by user ID
      if (filters.userId && log.user_id !== filters.userId) {
        return false;
      }
      
      // Filter by search term
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        const searchableFields = [
          log.entity_id,
          log.action,
          log.status,
          log.ip_address,
          log.user_email || '',
          log.user_name || '',
          JSON.stringify(log.details).toLowerCase()
        ];
        
        return searchableFields.some(field => field.toLowerCase().includes(searchTerm));
      }
      
      return true;
    });
  };

  const filteredLogs = applyFilters(auditLogs);

  const getEntityTypeIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'will':
        return <Users className="h-4 w-4" />;
      case 'auth':
        return <Lock className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'view':
        return <Eye className="h-4 w-4" />;
      case 'download':
        return <Download className="h-4 w-4" />;
      case 'create':
        return <FileText className="h-4 w-4" />;
      case 'update':
        return <Edit className="h-4 w-4" />;
      case 'delete':
        return <Trash className="h-4 w-4" />;
      case 'login':
        return <LogIn className="h-4 w-4" />;
      case 'logout':
        return <LogOut className="h-4 w-4" />;
      case 'share':
        return <Share className="h-4 w-4" />;
      case 'notarize':
        return <Shield className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Check className="h-3 w-3" />
            Success
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <X className="h-3 w-3" />
            Failed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const getAlertSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="h-3 w-3" />
            Critical
          </span>
        );
      case 'high':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <AlertTriangle className="h-3 w-3" />
            High
          </span>
        );
      case 'medium':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="h-3 w-3" />
            Medium
          </span>
        );
      case 'low':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <AlertCircle className="h-3 w-3" />
            Low
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {severity}
          </span>
        );
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <Card className="p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the audit dashboard.
          </p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" text="Loading audit data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card className="p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Audit Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchAuditData} icon={RefreshCw}>Retry</Button>
        </Card>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Activities</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalLogs}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Document Access</p>
              <p className="text-2xl font-bold text-gray-900">{stats.documentAccess}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Will Access</p>
              <p className="text-2xl font-bold text-gray-900">{stats.willAccess}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed Attempts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.failedAttempts}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </Card>
      </div>
      
      {/* Activity Chart and Alerts Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Trend</h3>
          <div className="h-64">
            <div className="flex h-full items-end">
              {stats.activityByDay.map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full max-w-[40px] bg-blue-500 rounded-t"
                    style={{ height: `${(day.count / Math.max(...stats.activityByDay.map(d => d.count))) * 100}%` }}
                  ></div>
                  <div className="text-xs text-gray-600 mt-2">{format(parseISO(day.date), 'MMM d')}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Alerts Summary</h3>
            <span className="text-sm text-gray-500">{alerts.length} total</span>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Critical</span>
              </div>
              <span className="font-semibold">{stats.alertsByLevel.critical}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-700">High</span>
              </div>
              <span className="font-semibold">{stats.alertsByLevel.high}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Medium</span>
              </div>
              <span className="font-semibold">{stats.alertsByLevel.medium}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Low</span>
              </div>
              <span className="font-semibold">{stats.alertsByLevel.low}</span>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => setActiveTab('alerts')}
            >
              View All Alerts
            </Button>
          </div>
        </Card>
      </div>
      
      {/* Top Users and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Users</h3>
          <div className="space-y-4">
            {stats.topUsers.map((user, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.user_email}</p>
                    <p className="text-xs text-gray-500">{user.count} activities</p>
                  </div>
                </div>
                <span className="text-sm font-semibold">#{index + 1}</span>
              </div>
            ))}
          </div>
        </Card>
        
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {auditLogs.slice(0, 5).map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {getEntityTypeIcon(log.entity_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">
                      {log.action.charAt(0).toUpperCase() + log.action.slice(1)} {log.entity_type}
                    </p>
                    <span className="text-xs text-gray-500">
                      {format(parseISO(log.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    {log.user_email || log.user_id} • {log.ip_address}
                  </p>
                  {log.blockchain_verification && (
                    <div className="mt-1 flex items-center gap-1">
                      <Shield className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-600">Blockchain Verified</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => setActiveTab('logs')}
            >
              View All Logs
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderLogsTab = () => (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search logs..."
            value={filters.searchTerm}
            onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
            icon={Search}
          />
        </div>
        <Button
          variant="outline"
          icon={Filter}
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? 'bg-gray-100' : ''}
        >
          Filters
        </Button>
        <Button
          variant="outline"
          icon={RefreshCw}
          onClick={fetchAuditData}
        >
          Refresh
        </Button>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entity Type
              </label>
              <div className="space-y-2">
                {['document', 'will', 'auth'].map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.entityType.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilters({
                            ...filters,
                            entityType: [...filters.entityType, type]
                          });
                        } else {
                          setFilters({
                            ...filters,
                            entityType: filters.entityType.filter(t => t !== type)
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <div className="space-y-2">
                {['view', 'download', 'create', 'update', 'login'].map((action) => (
                  <label key={action} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.action.includes(action)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilters({
                            ...filters,
                            action: [...filters.action, action]
                          });
                        } else {
                          setFilters({
                            ...filters,
                            action: filters.action.filter(a => a !== action)
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{action}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="space-y-2">
                {['success', 'failed', 'pending'].map((status) => (
                  <label key={status} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.status.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilters({
                            ...filters,
                            status: [...filters.status, status]
                          });
                        } else {
                          setFilters({
                            ...filters,
                            status: filters.status.filter(s => s !== status)
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{status}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  entityType: [],
                  action: [],
                  status: [],
                  dateRange: {
                    start: subDays(new Date(), 7),
                    end: new Date()
                  },
                  searchTerm: ''
                });
              }}
            >
              Reset Filters
            </Button>
            <Button
              onClick={() => setShowFilters(false)}
            >
              Apply Filters
            </Button>
          </div>
        </Card>
      )}
      
      {/* Logs Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verification
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    No logs found matching your filters
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(log.created_at), 'MMM d, yyyy h:mm:ss a')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {log.user_email || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {log.user_id.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {getActionIcon(log.action)}
                        <span className="text-sm text-gray-900 capitalize">{log.action}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {getEntityTypeIcon(log.entity_type)}
                        <span className="text-sm text-gray-900 capitalize">{log.entity_type}</span>
                        <span className="text-xs text-gray-500">{log.entity_id.substring(0, 6)}...</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(log.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip_address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.blockchain_verification ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <Shield className="h-4 w-4" />
                          <span className="text-xs">Verified</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLog(log)}
                        className="text-primary-600"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Audit Log Details</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={() => setSelectedLog(null)}
                />
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Time</p>
                    <p className="text-sm text-gray-900">
                      {format(parseISO(selectedLog.created_at), 'MMM d, yyyy h:mm:ss a')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">User</p>
                    <p className="text-sm text-gray-900">
                      {selectedLog.user_email || selectedLog.user_id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Action</p>
                    <p className="text-sm text-gray-900 capitalize">
                      {selectedLog.action}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Entity</p>
                    <p className="text-sm text-gray-900">
                      <span className="capitalize">{selectedLog.entity_type}</span>: {selectedLog.entity_id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="text-sm text-gray-900">
                      {getStatusBadge(selectedLog.status)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">IP Address</p>
                    <p className="text-sm text-gray-900">
                      {selectedLog.ip_address}
                    </p>
                  </div>
                </div>
                
                {selectedLog.blockchain_verification && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Blockchain Verification</p>
                        <p className="text-xs text-green-700 mt-1">
                          Transaction: {selectedLog.blockchain_verification.transaction_hash}
                        </p>
                        <p className="text-xs text-green-700">
                          Status: {selectedLog.blockchain_verification.status}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Details</p>
                  <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">User Agent</p>
                  <p className="text-xs text-gray-600 break-all bg-gray-50 p-3 rounded-lg">
                    {selectedLog.user_agent}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAlertsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Security Alerts</h3>
        <Button
          variant="outline"
          icon={RefreshCw}
          onClick={fetchAuditData}
        >
          Refresh
        </Button>
      </div>
      
      {/* Alerts List */}
      <div className="space-y-4">
        {alerts.length === 0 ? (
          <Card className="p-6 text-center">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Alerts</h3>
            <p className="text-gray-600">
              There are currently no security alerts that require your attention.
            </p>
          </Card>
        ) : (
          alerts.map((alert) => (
            <Card key={alert.id} className={`p-6 ${alert.is_resolved ? 'bg-gray-50' : ''}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  alert.severity === 'critical' ? 'bg-red-100' :
                  alert.severity === 'high' ? 'bg-orange-100' :
                  alert.severity === 'medium' ? 'bg-yellow-100' :
                  'bg-blue-100'
                }`}>
                  <AlertTriangle className={`h-5 w-5 ${
                    alert.severity === 'critical' ? 'text-red-600' :
                    alert.severity === 'high' ? 'text-orange-600' :
                    alert.severity === 'medium' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-semibold text-gray-900">{alert.message}</h4>
                      {getAlertSeverityBadge(alert.severity)}
                    </div>
                    <span className="text-sm text-gray-500">
                      {format(parseISO(alert.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-2">
                    Alert ID: {alert.id.substring(0, 8)}... • Audit Log: {alert.audit_log_id.substring(0, 8)}...
                  </p>
                  
                  {alert.is_resolved ? (
                    <div className="mt-4 flex items-center gap-2 text-green-600">
                      <Check className="h-4 w-4" />
                      <span className="text-sm font-medium">Resolved</span>
                    </div>
                  ) : (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResolveAlert(alert.id)}
                      >
                        Mark as Resolved
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  const renderReportsTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Audit Report</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={reportType === 'activity'}
                  onChange={() => setReportType('activity')}
                  className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Activity Report</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={reportType === 'security'}
                  onChange={() => setReportType('security')}
                  className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Security Report</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={reportType === 'compliance'}
                  onChange={() => setReportType('compliance')}
                  className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Compliance Report</span>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={reportFormat === 'pdf'}
                  onChange={() => setReportFormat('pdf')}
                  className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">PDF</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={reportFormat === 'csv'}
                  onChange={() => setReportFormat('csv')}
                  className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">CSV</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={reportFormat === 'json'}
                  onChange={() => setReportFormat('json')}
                  className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">JSON</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start Date</label>
              <input
                type="date"
                value={format(reportDateRange.start, 'yyyy-MM-dd')}
                onChange={(e) => setReportDateRange({
                  ...reportDateRange,
                  start: new Date(e.target.value)
                })}
                className="block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End Date</label>
              <input
                type="date"
                value={format(reportDateRange.end, 'yyyy-MM-dd')}
                onChange={(e) => setReportDateRange({
                  ...reportDateRange,
                  end: new Date(e.target.value)
                })}
                className="block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <Button
            onClick={handleGenerateReport}
            loading={isGeneratingReport}
            disabled={isGeneratingReport}
            className="w-full"
          >
            Generate Report
          </Button>
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Scheduled Reports</h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Format
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Run
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  Weekly Summary
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  Weekly
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  PDF
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {format(addDays(new Date(), 3), 'MMM d, yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary-600"
                  >
                    Edit
                  </Button>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  Monthly Compliance
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  Monthly
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  PDF
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {format(addDays(new Date(), 15), 'MMM d, yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary-600"
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reports</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Weekly Activity Report</p>
                <p className="text-xs text-gray-500">Generated on {format(subDays(new Date(), 3), 'MMM d, yyyy')}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              icon={Download}
            >
              Download
            </Button>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Security Audit Report</p>
                <p className="text-xs text-gray-500">Generated on {format(subDays(new Date(), 10), 'MMM d, yyyy')}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              icon={Download}
            >
              Download
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Audit Dashboard</h2>
            <p className="text-gray-600">Comprehensive audit trail and security monitoring</p>
          </div>
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <BarChart2 size={16} />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'logs'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Activity size={16} />
            Audit Logs
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'alerts'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <AlertTriangle size={16} />
            Alerts
            {alerts.filter(a => !a.is_resolved).length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                {alerts.filter(a => !a.is_resolved).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'reports'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <FileText size={16} />
            Reports
          </button>
        </nav>
      </div>
      
      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'logs' && renderLogsTab()}
        {activeTab === 'alerts' && renderAlertsTab()}
        {activeTab === 'reports' && renderReportsTab()}
      </motion.div>
    </div>
  );
};

// These components are used in the component but not defined
// Adding minimal implementations to make the code complete
const Edit = (props: any) => <Activity {...props} />;
const Trash = (props: any) => <Activity {...props} />;
const LogIn = (props: any) => <Activity {...props} />;
const LogOut = (props: any) => <Activity {...props} />;
const Share = (props: any) => <Activity {...props} />;
const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export default AuditDashboard;